import type React from 'react'
import { useCallback, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../components/skeleton/Card'
import { Button } from '../../../components/ui/Button'
import { BasicInfoStep } from './BasicInfoStep'
import { PasswordStep } from './PasswordStep'
import { ReviewStep } from './ReviewStep'
import { PaymentStep } from './PaymentStep'
import toast from 'react-hot-toast'
import { useCreateUser } from '../../../api/auth/useCreateUser'
import { useNavigate } from 'react-router-dom'
import { SUBSCRIPTION_PLANS } from '../../../lib/utils/subscriptionPlans'

interface SignUpFormData {
  first_name: string
  last_name: string
  email: string
  password: string
  confirmPassword: string
  phone?: string
  plan: 'basic' | 'pro'
}

export const SignUpPage: React.FC = () => {
  const [step, setStep] = useState(1)
  const methods = useForm<SignUpFormData>()
  const { mutateAsync: createUser } = useCreateUser()
  const navigate = useNavigate()

  const onSubmit = async (data: SignUpFormData) => {
    const toastId = toast.loading('Creating user')
    const plan = SUBSCRIPTION_PLANS.find((p) => p.name === data.plan)

    if (!plan) {
      toast.error('Invalid subscription')
      return
    }

    await createUser(
      {
        first_name: data?.first_name,
        last_name: data?.last_name,
        phone: data?.phone,
        email: data?.email,
        password: data?.password,
      },
      {
        onSuccess: () => {
          toast.success('User created!', { id: toastId })
        },
        onError: (e) => {
          toast.error(
            // @ts-ignore
            e?.response?.data?.error ? e?.response?.data?.error : 'An error occured!',
            { id: toastId },
          )
        },
      },
    )
    window.addEventListener(`error`, (e) => alert(e.toString()))
    window.location.href = `${plan.link}?prefilled_email=${data.email}`
    // window.open(`${plan.link}?prefilled_email=${data.email}`, '_blank')

    navigate('/auth/login')
  }

  const { handleSubmit, trigger } = methods

  const nextStep = async (): Promise<void> => {
    let isValid = false
    switch (step) {
      case 1:
        isValid = await trigger(['first_name', 'last_name', 'email'])
        break
      case 2:
        isValid = await trigger(['password', 'confirmPassword'])
        break
      case 3:
        isValid = await trigger(['phone'])
        break
      case 4:
        isValid = await trigger(['plan'])
        break
      default:
        isValid = true
    }

    if (isValid) setStep((prev) => Math.min(prev + 1, 5))
  }
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1))

  const renderStep = useCallback(() => {
    switch (step) {
      case 1:
        return <BasicInfoStep />
      case 2:
        return <PasswordStep />
      case 3:
        return <ReviewStep />
      case 4:
        return <PaymentStep />
      default:
        return null
    }
  }, [step])

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10 h-screen bg-foreground/50 dark:bg-slate-700">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">Sign Up</CardTitle>
          <CardDescription className="text-center">
            Create your account in a few easy steps
          </CardDescription>
        </CardHeader>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <CardContent>{renderStep()}</CardContent>
            <CardDescription className="text-center pb-4">
              <div className="flex flex-row items-center justify-center gap-1 content-center text-center">
                <span>Already have an account?</span>
                <a className="underline" href="login">
                  Sign in
                </a>
              </div>
            </CardDescription>
            <CardFooter className="flex justify-between">
              {step > 1 ? (
                <Button type="button" variant="outline" onClick={prevStep}>
                  Previous
                </Button>
              ) : (
                <div></div>
              )}
              {step < 4 ? (
                <Button
                  type="button"
                  variant={'outline'}
                  onClick={(e) => {
                    e.preventDefault()
                    nextStep()
                  }}>
                  Next
                </Button>
              ) : (
                <Button type="submit">Submit</Button>
              )}
            </CardFooter>
          </form>
        </FormProvider>
      </Card>
    </div>
  )
}
